import { getHelper } from '@/devtools/utils';
import { SIMPLE_CHOICE } from '@/features/ChoicesLists/Simple/types';
import { VersicherungsnehmerPropertyContractSchema } from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import type { SpartenConfig } from '@/features/Sparten/types';
import { formatCurrency } from '@/utils/formatters/euro';
import { formatYears } from '@/utils/formatters/years';

import { complexSelectionFormatter } from '@/components/form-elements/ComplexSelect/helper';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import { SEARCH_DATA_CONTEXT } from '@/features/Global/types';
import { buInputDehydrationDefinitions } from '@/features/Sparten/bu/inputDehydration';
import {
  InputStore,
  useBuInputStore,
} from '@/features/Sparten/bu/useInputStore';
import { keepDataFromQuoteBaseProvidentDefinition } from '@/features/Sparten/shared/provident/keepDataFromQuoteBaseProvidentDefinition';
import { providentQuotePreInputMergeHandler } from '@/features/Sparten/shared/provident/quotePreInputMergeHandler';
import { SORTABLE_ATTRIBUTES } from '@/features/Tariffs/sorting';
import { dehydrateRequestData } from '@/utils/dehydrateRequestData/dehydrateRequestData';
import boolean from '@/utils/formatters/boolean';
import { formatChoice } from '@/utils/formatters/simpleChoice';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import image from './assets/bg.jpg';
import { buVersicherungsangabenConfig } from '@/features/Sparten/bu/versicherungsangabenConfig';

export const config: SpartenConfig = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/bu',
  props: {
    name: 'Berufsunfähigkeitsversicherung',
    sparte: 'bu',
    image,
  },
  general: {
    distribution: {
      channel: false,
      pooling: false,
    },

    maxSimultaneousCount: 3,

    versicherungsangabenConfig: buVersicherungsangabenConfig,

    clientFields: {
      versicherungsnehmer: {
        geburtsdatum: 'geburtsdatum',
      },
      versichertePerson: {
        geburtsdatum: 'geburtsdatum',
        anteilTaetigkeitBuero: 'anteilTaetigkeitBuero',
        anzahlKinder: 'anzahlKinder',
        gewicht: 'gewicht',
        groesse: 'groesse',
        berufsgruppe: 'berufsgruppe',
        hoechsterBildungsabschluss: 'hoechsterBildungsabschluss',
        familienstand: 'familienstand',
        personalverantwortung: 'personalverantwortung',
        nichtraucher: 'nichtraucher',
      },
    },

    calculationRelevantEntityFields: {
      [ENTITY_TYPE.CLIENT]: {
        versicherungsnehmer: ['geburtsdatum'],
        versichertePerson: [
          'geburtsdatum',
          'anteilTaetigkeitBuero',
          'anzahlKinder',
          'gewicht',
          'groesse',
          'berufsgruppe',
          'hoechsterBildungsabschluss',
          'familienstand',
          'personalverantwortung',
          'nichtraucher',
        ],
      },
    },

    profileFields: [
      'zahlweise',
      'beitragsdynamik',
      'beitragsendalter',
      'niedrigerAnfangsbeitrag',
      'rentensteigerung',
      'ueberschusssystem',
    ],

    getSortableAttributes: () => [
      SORTABLE_ATTRIBUTES.ERFUELLUNGSGRAD,
      SORTABLE_ATTRIBUTES.GESELLSCHAFT,
      SORTABLE_ATTRIBUTES.MAX_MOEGLICHER_BEITRAG,
      SORTABLE_ATTRIBUTES.MONATLICHE_RENTE,
      SORTABLE_ATTRIBUTES.TARIFNAME,
    ],

    buildInputDataObject: (context: SEARCH_DATA_CONTEXT, dehydrate = false) => {
      const state = useBuInputStore().$state as InputStore;

      const preparedData = useStorableState().create(state);

      if (context === SEARCH_DATA_CONTEXT.TARIFF && dehydrate) {
        return dehydrateRequestData(
          preparedData,
          buInputDehydrationDefinitions,
        );
      }

      return preparedData;
    },

    buildContractDataObject: (state) => state,

    keepDataFromQuote: {
      clients: {
        versicherungsnehmer: [...keepDataFromQuoteBaseProvidentDefinition],
        versichertePerson: [...keepDataFromQuoteBaseProvidentDefinition],
      },
    },

    quotePreInputMergeCallback: (state) =>
      providentQuotePreInputMergeHandler(state),

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        map.set('maxMoeglicherBeitrag', {
          label: 'max. möglicher Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        {
          label: 'monatliche Rente',
          fieldPath: 'monatlicheRente',
          formatter: formatCurrency,
        },
        {
          label: 'reduzierter Anfangsbeitrag',
          fieldPath: 'niedrigerAnfangsbeitrag',
          formatter: boolean,
        },
        {
          label: 'Endalter',
          fieldPath: 'versicherungsendalter',
          formatter: formatYears,
          inputFieldAsFallback: true,
        },
        {
          label: 'Zahlweise',
          fieldPath: 'zahlweise',
          formatter: formatChoice(SIMPLE_CHOICE.PAYMENT_PERIOD),
        },
        {
          label: 'garantierte Steigerung',
          fieldPath: 'rentensteigerung',
          formatter: (val: string) => `↗ ${val}%`,
          inputFieldAsFallback: true,
        },
        {
          label: 'Beruf',
          inputFieldPath: 'berufComplex',
          fieldPath: 'berufsbezeichnung',
          formatter: complexSelectionFormatter,
        },
      ],
    },

    tariffComponent: () =>
      import(
        '@/features/Sparten/bu/components/TariffDetails/TariffDetails.vue'
      ),

    baseTariffComponent: () =>
      import('@/features/Sparten/bu/components/TariffDetails/BaseTariff.vue'),

    devtools: {
      directAccess: {
        input: {
          icon: 'edit',
          setup: async () => getHelper('bu').resetState(),
        },
        offers: {
          icon: 'list',
          setup: async () => getHelper('bu').selectAllFeatures(),
        },
        comparison: {
          icon: 'compare_arrows',
          setup: async () => getHelper('bu').loadTariffs(),
        },
        contract: {
          icon: 'description',
          setup: async () => getHelper('bu').showContract(),
        },
      },
    },

    contractVnSchema: () => VersicherungsnehmerPropertyContractSchema,
  },

  steps: {
    input: {
      component: () => import('@/features/Sparten/bu/views/InputView.vue'),
    },

    offers: {
      component: () => import('@/features/Sparten/bu/views/Tariffs.vue'),
    },

    comparison: {
      component: () => import('@/features/Sparten/bu/views/Comparison.vue'),
      meta: {
        showContractButton: true,
      },
    },

    contract: {
      component: () => import('@/features/Sparten/bu/views/ContractView.vue'),
    },

    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};
